import './Timeline.scss'

import { useContext, useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import moment from 'moment'
import TimelineEvent from './TimelineEvent'
import {
  EVENT_ACTION_PAGEVIEW,
  EVENT_CATEGORY_COURSE,
  EVENT_CATEGORY_DAILY_REMBLE,
  EVENT_CATEGORY_TODAY,
  getUserEvents
} from 'components/common/api/UserEvents'
import CourseContext from '../../common/contexts/CourseContext'
import { ROUTE_DAILY_REMBLE, ROUTE_PROFILE } from '../../common/helpers/Paths'
import { getDailyRembleVideos } from '../../common/api/DailyRemble'
import Loading from '../../../templates/Loading'

const filters = [
  {
    title: 'Today',
    predicate: x => moment().isSame(x, 'day'),
    matches: {}
  },
  {
    title: 'Yesterday',
    predicate: x => moment().subtract(1, 'day').isSame(x, 'day'),
    matches: {}
  },
  {
    title: 'This Week',
    predicate: x => moment().isSame(x, 'week'),
    matches: {}
  },
  {
    title: 'This Month',
    predicate: x => moment().isSame(x, 'month'),
    matches: {}
  },
  {
    title: 'This Year',
    predicate: x => moment().isSame(x, 'year'),
    matches: {}
  }
]

/**
 * Timeline component. Used to display user history
 */
export default function Timeline({ cache, setReturnPath, setHeaderTitle }) {
  const { courses } = useContext(CourseContext)

  const [groupedMoments, setGroupedMoments] = useState([])
  const [loading, setLoading] = useState(true)
  const [allCourses, setAllCourses] = useState([])
  const [userEvents, setUserEvents] = useState([])
  const [dailyRembleVideos, setDailyRembleVideos] = useState([])

  /**
   * Effect to get the daily remble videos
   */
  useEffect(() => {
    // Get Daily Remble Videos
    async function getDailyRembleVideosAsync() {
      // Check cache first
      const result = await cache.tryGetAsyncResult(
        ROUTE_DAILY_REMBLE,
        getDailyRembleVideos,
        this,
        { per_page: 100 }
      )
      setDailyRembleVideos(result || [])
    }

    // Run it
    getDailyRembleVideosAsync()
  }, [cache])

  useEffect(() => {
    setHeaderTitle('History')
    setReturnPath(ROUTE_PROFILE)

    if (courses) {
      setAllCourses(courses)
      setLoading(false)
    } else {
      setLoading(true)
    }

    // Get events data, but don't block the thread
    getUserEvents({
      action: EVENT_ACTION_PAGEVIEW,
      limit: -1,
      exclude_categories: [EVENT_CATEGORY_TODAY]
    }).then(events => {
      setUserEvents(events)
    })
  }, [cache, courses, allCourses, setReturnPath, setHeaderTitle])

  const tryGetTimelineEvent = timelineEvent => {
    let foundEvent = null

    // Define our category types
    const lookups = {}

    // Courses
    lookups[EVENT_CATEGORY_COURSE] = courseId =>
      allCourses.find(course => course.id === courseId)

    // Daily Remble
    lookups[EVENT_CATEGORY_DAILY_REMBLE] = dailyRembleId =>
      dailyRembleVideos.find(video => video.id === dailyRembleId)

    // Try to get the event
    try {
      const lookupCategory = lookups[timelineEvent.category]
      const itemId = timelineEvent.label ?? timelineEvent.value

      // Do we have a lookup for this category?
      if (lookupCategory) foundEvent = lookupCategory(parseInt(itemId))
    } catch (e) {
      console.error(e)
    }

    return foundEvent
  }

  useEffect(() => {
    // Copy the filters array
    let matchedMoments = [...filters]

    // Break userEvents out into groups
    userEvents.forEach(event => {
      // See which filter this event fits into
      for (let i = 0; i < filters.length; i++) {
        const itemId = event.label || event.value
        // If value isn't a number, then it's some other course page
        const match =
          !isNaN(parseInt(itemId)) && filters[i].predicate(event.created_on)

        // Do we have a match? If so, break out of the loop
        if (match) {
          matchedMoments[i].matches[itemId] = event
          break
        }
      }
    })

    // Now that we've grouped them, let's remove duplicates, starting with the year
    for (let i = matchedMoments.length - 1; i > 0; --i) {
      const matches = matchedMoments[i].matches

      // Go through all earlier periods
      for (let j = i - 1; j >= 0; j--) {
        const earlierMatches = matchedMoments[j].matches

        // Does this item existing in an earlier group?
        Object.keys(matches).forEach(match => {
          // If it exists in an earlier group, drop it from this one
          if (earlierMatches[match]) delete matchedMoments[i].matches[match]
        })
      }
    }

    setGroupedMoments(matchedMoments)
  }, [allCourses, userEvents, dailyRembleVideos, groupedMoments.length])

  // Return early if we don't have any userEvents to process
  if (!groupedMoments.length) return null

  return (
    <Loading isLoaded={loading}>
      <Container>
        <div className='timeline-wrapper'>
          {groupedMoments.map(group => {
            const groupMatches = Object.keys(group.matches)

            return (
              groupMatches.length > 0 && (
                <div key={group.title}>
                  <h3>{group.title}</h3>
                  <Row xs={1} md={2} lg={3}>
                    {groupMatches.map((key, i) => {
                      const timelineEvent = tryGetTimelineEvent(
                        group.matches[key]
                      )
                      return (
                        timelineEvent && (
                          <Col key={key}>
                            <TimelineEvent
                              style={{ animationDelay: `${0.05 * i}s` }}
                              event={timelineEvent}
                            />
                          </Col>
                        )
                      )
                    })}
                  </Row>
                </div>
              )
            )
          })}
        </div>
      </Container>
    </Loading>
  )
}
